import { useState, useCallback } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { transformStringToPath } from '../../../../../helpers/transformStringToPath';
import { useRootSelector } from '../../../../../store/hooks';
import { Anchor } from '../../../../shared/Anchor';
import { selectLocationConfig } from '../../../../../store/features/locationConfig';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  IGFEntryProfileImageProps,
  IGFEntryProfileProps,
} from '../../../../../store/features/igfEntries';

import styles from './EntryCard.module.scss';

export interface EntryCardProps {
  name: IGFEntryProfileProps['name'];
  creator: IGFEntryProfileProps['creator'];
  image: Pick<IGFEntryProfileImageProps, 'thumb_450' | 'caption'>;
  description: IGFEntryProfileProps['description'];
}

export default function EntryCard(props: EntryCardProps) {
  const { pathname } = useRootSelector(selectLocationConfig);
  const { t } = useTranslation();
  const {
    name,
    creator,
    description,
    image: {
      // eslint-disable-next-line
      thumb_450: imageSrc,
      caption,
    } = {},
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const handleShowDetails = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  return (
    <div className={styles.wrapper}>
      <Anchor
        to={transformStringToPath(name)}
        pathPrefix={pathname}
        style={{ backgroundImage: `url(${imageSrc})` }}
        data-src={caption}
        className={styles.entryImageWrapper}
      />
      <div className={styles.entryDetailsWrapper}>
        <div className={styles.entryDetailsInfo}>
          <Anchor
            to={transformStringToPath(name)}
            pathPrefix={pathname}
            className={styles.cardAnchor}
          >
            <div className={styles.entryTitle}>{name}</div>
            {creator && (
              <p className={styles.author}>
                {t('site.year.page.card.by', { 0: creator })}
              </p>
            )}
          </Anchor>
        </div>
        <div className={styles.entryDescriptionWrapper}>
          <button
            type="button"
            className={styles.entryDetailsBtn}
            onClick={handleShowDetails}
          >
            <ExpandMoreIcon
              className={cn(styles.expandIcon, {
                [styles.expanded]: isOpen,
              })}
            />
            {t('site.year.page.card.view-entry.link')}
          </button>
          <div
            className={cn(styles.entryDescription, {
              [styles.opened]: isOpen,
            })}
          >
            {description}
          </div>
        </div>
      </div>
    </div>
  );
}
